// src/pages/PaySlip.js
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import html2pdf from 'html2pdf.js';
import '../letterstyles/PaySlip.css';

const PaySlip = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const slipRef = useRef();
  const user = state?.user;

  const superAdminId = Number(localStorage.getItem('adminuserId')) || 0;

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const handlePrint = useReactToPrint({
    content: () => slipRef.current,
    documentTitle: `${user?.name}_Pay_Slip`,
  });

  const handleDownload = () => {
    const opt = {
      margin: 0,
      filename: `${user?.name}_Pay_Slip.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    };
    html2pdf().set(opt).from(slipRef.current).save();
  };

  // Fetch company details from API
  useEffect(() => {
    if (!user) {
      alert("No user data found!");
      navigate('/');
    }

    const fetchCompany = async () => {
      try {
        setLoading(true);
        const response = await fetch(
          `https://payyug.in/api/get-company?superAdminId=${superAdminId}`
        );
        const data = await response.json();

        if (response.ok && data) {
          if (data.logo) {
            data.logoUrl = `https://payyug.in/${data.logo.replace(/^images\//, '')}`;
          }
          setCompany(data);
        } else {
          console.error('Failed to fetch company data');
        }
      } catch (err) {
        console.error('Error fetching company details:', err);
      } finally {
        setLoading(false);
      }
    };

    if (superAdminId) fetchCompany();
  }, [navigate, user, superAdminId]);

  if (!user) return null;
  if (loading) return <p>Loading company details...</p>;

  // Example salary data; in real scenario, fetch this from API
  const salary = {
    basic: 15000,
    hra: 5000,
    conveyance: 2000,
    deductions: 2500,
  };
  salary.net = salary.basic + salary.hra + salary.conveyance - salary.deductions;

  return (
    <div className="letter-wrapper">
      <div className="letter-actions">
        <button className="letter-action-button" onClick={handlePrint}>🖨️ Print</button>
        <button className="letter-action-button" onClick={handleDownload}>⬇️ Download</button>
      </div>

      <div className="pay-slip" ref={slipRef}>
        <div className="letter-header">
          <img 
            src={company?.logoUrl || '/default-logo.png'} 
            alt="Company Logo" 
            className="letter-logo" 
          />
          <div>
            <h2>{company?.name}</h2>
            <p>Email: {company?.email}</p>
            <p>Address: {company?.address}</p>
          </div>
        </div>

        <div className="slip-body">
          <h3>Pay Slip for {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}</h3>
          <p><strong>Employee Name:</strong> {user.fullName}</p>
          <p><strong>Employee ID:</strong> {user.userId}</p>
          <p><strong>Designation:</strong> {user.roleName}</p>
          <p><strong>Branch:</strong> {company?.address}</p>

          <table className="salary-table">
            <thead>
              <tr>
                <th>Description</th>
                <th>Amount (INR)</th>
              </tr>
            </thead>
            <tbody>
              <tr><td>Basic Salary</td><td>{salary.basic}</td></tr>
              <tr><td>HRA</td><td>{salary.hra}</td></tr>
              <tr><td>Conveyance</td><td>{salary.conveyance}</td></tr>
              <tr><td>Deductions</td><td>-{salary.deductions}</td></tr>
              <tr className="net-row"><td><strong>Net Pay</strong></td><td><strong>{salary.net}</strong></td></tr>
            </tbody>
          </table>
        </div>

        <div className="letter-footer">
          <hr />
          <p>{company?.name} | CIN: {company?.cin}</p>
          <p>{company?.email} | {company?.contact}</p>
        </div>
      </div>
    </div>
  );
};

export default PaySlip;
